import { useCallback, useMemo } from "react";
import { t } from "@/i18n-js/instance";
import { useSaveReusableBlockModal } from "@/react/components/SettingsApp/Site/Pages/Edit/SaveReusableBlockModal";
import { useIsReusableBlocksEnabled } from "@/react/components/SettingsApp/Site/Pages/hooks/useIsReusableBlocksEnabled";
import { defaultData as containerDefaultData } from "@/react/components/shared/BuilderUI/DefaultBlocks/Content/ContainerBlock/ContainerBlockDefaultData";
import { defaultData as gridDefaultData } from "@/react/components/shared/BuilderUI/DefaultBlocks/Content/GridBlock/GridBlockDefaultData";
import { defaultData as stackDefaultData } from "@/react/components/shared/BuilderUI/DefaultBlocks/Content/StackBlock/StackBlockDefaultData";
import { defaultData as sectionDefaultData } from "@/react/components/shared/BuilderUI/DefaultBlocks/SectionBlock/SectionBlockDefaultData";
import { useSelectedBlock } from "./useBuilderStore";

export const blocksThatCanBeSavedAsReusableBlock = [
  containerDefaultData.type,
  sectionDefaultData.type,
  stackDefaultData.type,
  gridDefaultData.type,
];

export const useSelectedBlockActions = () => {
  const {
    canMoveUp,
    canMoveDown,
    moveSelectedBlock,
    removeSelectedBlock,
    duplicateSelectedBlock,
    resetBlockStyle,
    copyBlockId,
    selectedBlock,
  } = useSelectedBlock();
  const isReusableBlocksEnabled = useIsReusableBlocksEnabled();
  const { show: showSaveReusableBlockModal } = useSaveReusableBlockModal();

  const isSaveAsReusableBlockEnabled =
    selectedBlock &&
    isReusableBlocksEnabled &&
    blocksThatCanBeSavedAsReusableBlock.includes(selectedBlock?.type);

  const onRemoveHandler = useCallback(() => {
    // TODO: Add confirmation modal
    removeSelectedBlock();
  }, [removeSelectedBlock]);

  return useMemo(
    () => [
      ...(isSaveAsReusableBlockEnabled
        ? [
            {
              label: t("builder_ui.editor.save_as_reusable_block"),
              value: "save_as_reusable_block",
              onClick: () =>
                showSaveReusableBlockModal({ block: selectedBlock }),
            },
          ]
        : []),
      {
        label: t("builder_ui.editor.move_up"),
        value: "move_up",
        isDisabled: !canMoveUp,
        onClick: () => moveSelectedBlock("up"),
      },
      {
        label: t("builder_ui.editor.move_down"),
        value: "move_down",
        isDisabled: !canMoveDown,
        onClick: () => moveSelectedBlock("down"),
      },
      {
        label: t("builder_ui.editor.duplicate"),
        value: "duplicate",
        onClick: duplicateSelectedBlock,
      },
      {
        label: t("builder_ui.editor.copy_block_id"),
        value: "copy_block_id",
        onClick: copyBlockId,
      },
      {
        label: t("builder_ui.editor.reset_styles"),
        value: "reset_styles",
        color: "text-v2-danger",
        onClick: resetBlockStyle,
      },
      {
        label: t("builder_ui.editor.remove"),
        value: "remove",
        color: "text-v2-danger",
        onClick: onRemoveHandler,
      },
    ],
    [
      canMoveDown,
      canMoveUp,
      duplicateSelectedBlock,
      moveSelectedBlock,
      onRemoveHandler,
      selectedBlock,
      resetBlockStyle,
      copyBlockId,
    ],
  );
};
